import type {ReactNode, RefObject} from 'react';
import React, {Children, useEffect, useRef, useState} from 'react';

import {adjustFontSizeToFit} from '@edume/bento/textUtil.browser';

import type {TextSizes} from '../TextComponents/Text';

import typographyStyles from '../../resources/styles/typography.module.scss';

type Props = {
  children: ReactNode;
  maxPxWidth: number;
  startingFontSize: TextSizes;
  minimumFontSize: TextSizes;
};

type EnrichedChild = {
  containerRef: RefObject<HTMLDivElement | HTMLButtonElement>;
  contentRef: RefObject<HTMLDivElement | HTMLSpanElement>;
  children: ReactNode;
};

const extractFontSize = (size: TextSizes) => {
  const cssString = typographyStyles[`bodyFontSize-${size}`];
  // @ts-ignore: TODO: Improve CSS typing or refactor
  return Number(cssString.split('rem')[0]);
};

// This component will reduce the font size in the children until they fit the specified width
const ShrinkText = ({
  children,
  maxPxWidth,
  startingFontSize,
  minimumFontSize,
}: Props) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const startingRemFontSize = extractFontSize(startingFontSize);
  const minimumRemFontSize = extractFontSize(minimumFontSize);

  // Need to trigger a re-render when the ref changes for this to work in bubble lessons
  const [, setHasRefChanged] = useState(false);
  useEffect(() => {
    setHasRefChanged(true);
  }, [containerRef]);

  if (containerRef?.current) {
    adjustFontSizeToFit({
      containerElement: containerRef.current,
      contentElement: contentRef?.current,
      maxPxWidth,
      startingRemFontSize,
      minimumRemFontSize,
    });
  }

  // This pattern allows us to pass props into children without caring what the child is
  return (
    <>
      {Children.map(children, (child) => {
        if (React.isValidElement<EnrichedChild>(child)) {
          return React.cloneElement(child, {
            containerRef,
            contentRef,
          });
        }
        return child;
      })}
    </>
  );
};

export default ShrinkText;
